import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseUrl = process.env.REACT_APP_API_URL || '/api';

export const branchApi = createApi({
  reducerPath: 'branchApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Branch'],
  endpoints: (builder) => ({
    // Get all branches
    getBranches: builder.query({
      query: (params = {}) => ({
        url: '/branches',
        params,
      }),
      providesTags: ['Branch'],
    }),

    // Get branch by ID
    getBranch: builder.query({
      query: (id) => `/branches/${id}`,
      providesTags: (result, error, id) => [{ type: 'Branch', id }],
    }),

    // Create branch
    createBranch: builder.mutation({
      query: (branch) => ({
        url: '/branches',
        method: 'POST',
        body: branch,
      }),
      invalidatesTags: ['Branch'],
    }),

    // Update branch
    updateBranch: builder.mutation({
      query: ({ id, ...branch }) => ({
        url: `/branches/${id}`,
        method: 'PUT',
        body: branch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Branch', id },
        'Branch',
      ],
    }),

    // Delete branch
    deleteBranch: builder.mutation({
      query: (id) => ({
        url: `/branches/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Branch'],
    }),

    // Get branch stats
    getBranchStats: builder.query({
      query: () => '/branches/stats',
      providesTags: ['Branch'],
    }),

    // Update branch working hours
    updateBranchWorkingHours: builder.mutation({
      query: ({ id, workingHours }) => ({
        url: `/branches/${id}/working-hours`,
        method: 'PUT',
        body: { workingHours },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Branch', id },
        'Branch',
      ],
    }),

    // Update branch breaks
    updateBranchBreaks: builder.mutation({
      query: ({ id, breaks }) => ({
        url: `/branches/${id}/breaks`,
        method: 'PUT',
        body: { breaks },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Branch', id },
        'Branch',
      ],
    }),

    // Get branch attendance report
    getBranchAttendanceReport: builder.query({
      query: ({ id, params = {} }) => ({
        url: `/branches/${id}/attendance-report`,
        params,
      }),
      providesTags: ['Branch'],
    }),

    // Get my branch (for current user)
    getMyBranch: builder.query({
      query: () => '/branches/my-branch',
      providesTags: ['Branch'],
    }),
  }),
});

export const {
  useGetBranchesQuery,
  useGetBranchQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
  useGetBranchStatsQuery,
  useUpdateBranchWorkingHoursMutation,
  useUpdateBranchBreaksMutation,
  useGetBranchAttendanceReportQuery,
  useGetMyBranchQuery,
} = branchApi;
